/**
 * Trail review regions for a RegionalModerator.
 *
 * A regional moderator reviews trail route edits only for trails inside these regions: a box drawn
 * on the map (click two opposite corners), or everything in one park (by the park adventure's id).
 * Regions do nothing without the RegionalModerator role above, and removing the role leaves them
 * inert rather than deleting them.
 */

import { useMemo, useState } from 'react'
import { Rectangle, useMapEvents } from 'react-leaflet'
import {
  Alert,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { Delete, Map as MapIcon } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { MapContainer } from '@components/map/MapContainer'
import { adminApi } from '@api/endpoints/admin'
import type { CreateModeratorRegionRequest, ModeratorRegionDto } from '@api/types/admin'
import { errorMessage } from './userFormatting'

type Corner = { lat: number; lng: number }

/** Two map clicks make a box; a third starts a new one. */
function CornerPicker({ corners, onChange }: { corners: Corner[]; onChange: (corners: Corner[]) => void }) {
  useMapEvents({
    click: (event) => {
      const next = { lat: Number(event.latlng.lat.toFixed(5)), lng: Number(event.latlng.lng.toFixed(5)) }
      onChange(corners.length >= 2 ? [next] : [...corners, next])
    },
  })
  return null
}

export function ModeratorRegionsEditor({ userId, hasRole }: { userId: string; hasRole: boolean }) {
  const queryClient = useQueryClient()
  const key = ['admin', 'users', userId, 'moderator-regions']
  const { data: regions = [], isLoading, error: loadError, refetch } = useQuery({
    queryKey: key,
    queryFn: () => adminApi.getModeratorRegions(userId),
  })

  const [kind, setKind] = useState<'box' | 'park'>('box')
  const [name, setName] = useState('')
  const [corners, setCorners] = useState<Corner[]>([])
  const [parkId, setParkId] = useState('')
  // A region is a hand-drawn box or a park id; removing one by a slipped tap loses that work.
  const [removeTarget, setRemoveTarget] = useState<ModeratorRegionDto | null>(null)

  const box = useMemo(() => {
    if (corners.length < 2) return null
    const [a, b] = corners
    return {
      minLatitude: Math.min(a.lat, b.lat),
      maxLatitude: Math.max(a.lat, b.lat),
      minLongitude: Math.min(a.lng, b.lng),
      maxLongitude: Math.max(a.lng, b.lng),
    }
  }, [corners])

  const create = useMutation({
    mutationFn: () => {
      const request: CreateModeratorRegionRequest =
        kind === 'box' && box ? { name: name.trim(), ...box } : { name: name.trim(), parkAdventureId: parkId.trim() }
      return adminApi.createModeratorRegion(userId, request)
    },
    onSuccess: () => {
      setName('')
      setCorners([])
      setParkId('')
      queryClient.invalidateQueries({ queryKey: key })
    },
  })

  const remove = useMutation({
    mutationFn: (regionId: string) => adminApi.deleteModeratorRegion(userId, regionId),
    onSuccess: () => {
      setRemoveTarget(null)
      return queryClient.invalidateQueries({ queryKey: key })
    },
  })

  const canCreate = name.trim().length > 0 && (kind === 'box' ? !!box : /^[0-9a-f-]{36}$/i.test(parkId.trim()))

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MapIcon fontSize="small" /> Trail review regions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        A regional moderator reviews trail route edits only inside these regions.
      </Typography>
      {!hasRole && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          These have no effect until the user has the RegionalModerator role.
        </Alert>
      )}

      {loadError && (
        <Alert
          severity="error"
          sx={{ mb: 1.5 }}
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          {errorMessage(loadError, 'Failed to load regions')}
        </Alert>
      )}
      {remove.error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {errorMessage(remove.error, 'Failed to remove region')}
        </Alert>
      )}

      {isLoading || loadError ? null : regions.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          No regions yet.
        </Typography>
      ) : (
        <List dense sx={{ mb: 1.5 }}>
          {regions.map((region) => (
            <ListItem
              key={region.id}
              secondaryAction={
                <IconButton edge="end" aria-label={`Remove ${region.name}`} onClick={() => setRemoveTarget(region)} disabled={remove.isPending}>
                  <Delete fontSize="small" />
                </IconButton>
              }
            >
              <ListItemText
                primary={region.name}
                secondary={
                  region.parkAdventureId
                    ? `Park: ${region.parkTitle ?? region.parkAdventureId}`
                    : `${region.minLatitude}, ${region.minLongitude} → ${region.maxLatitude}, ${region.maxLongitude}`
                }
              />
            </ListItem>
          ))}
        </List>
      )}

      <Stack spacing={1.5}>
        <ToggleButtonGroup exclusive size="small" value={kind} onChange={(_e, next) => next && setKind(next)}>
          <ToggleButton value="box">Map box</ToggleButton>
          <ToggleButton value="park">Park</ToggleButton>
        </ToggleButtonGroup>

        <TextField size="small" label="Region name" value={name} onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 100 }} />

        {kind === 'box' ? (
          <>
            <Typography variant="caption" color="text.secondary">
              Click two opposite corners on the map. A third click starts again.
            </Typography>
            <Box sx={{ height: 320, borderRadius: 1, overflow: 'hidden' }}>
              <MapContainer center={[31.0, -99.0]} zoom={5} height={320}>
                <CornerPicker corners={corners} onChange={setCorners} />
                {box && (
                  <Rectangle
                    bounds={[
                      [box.minLatitude, box.minLongitude],
                      [box.maxLatitude, box.maxLongitude],
                    ]}
                    pathOptions={{ color: '#0277bd', weight: 2 }}
                  />
                )}
              </MapContainer>
            </Box>
            {box && (
              <Typography variant="caption">
                {box.minLatitude}, {box.minLongitude} → {box.maxLatitude}, {box.maxLongitude}
              </Typography>
            )}
          </>
        ) : (
          <TextField
            size="small"
            label="Park adventure id"
            helperText="Every trail whose parent is this park. Copy the id from the park's page URL."
            value={parkId}
            onChange={(e) => setParkId(e.target.value)}
          />
        )}

        {create.error && <Alert severity="error">{errorMessage(create.error, 'Failed to add region')}</Alert>}

        <Box>
          <Button variant="contained" size="small" disabled={!canCreate || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? 'Adding…' : 'Add region'}
          </Button>
        </Box>
      </Stack>

      <ConfirmDialog
        open={!!removeTarget}
        title="Remove region?"
        message={removeTarget ? `This moderator will stop reviewing trail edits in "${removeTarget.name}".` : undefined}
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={() => removeTarget && remove.mutate(removeTarget.id)}
        onClose={() => setRemoveTarget(null)}
      />
    </Box>
  )
}
